import type { ValorantRank } from 'generated/openapi';

import { imageKitUrlEndpoint } from '@/integrations/image-kit/image-kit';

// Rank order mapping (0 = Unranked, 27 = Radiant)
export const rankOrderMap: Record<ValorantRank, number> = {
  UNRANKED: 0,
  IRON_1: 3,
  IRON_2: 4,
  IRON_3: 5,
  BRONZE_1: 6,
  BRONZE_2: 7,
  BRONZE_3: 8,
  SILVER_1: 9,
  SILVER_2: 10,
  SILVER_3: 11,
  GOLD_1: 12,
  GOLD_2: 13,
  GOLD_3: 14,
  PLATINUM_1: 15,
  PLATINUM_2: 16,
  PLATINUM_3: 17,
  DIAMOND_1: 18,
  DIAMOND_2: 19,
  DIAMOND_3: 20,
  ASCENDANT_1: 21,
  ASCENDANT_2: 22,
  ASCENDANT_3: 23,
  IMMORTAL_1: 24,
  IMMORTAL_2: 25,
  IMMORTAL_3: 26,
  RADIANT: 27,
};

/**
 * Type guard to check if a value is a valid ValorantRank
 */
export function isValorantRank(value: unknown): value is ValorantRank {
  return typeof value === 'string' && value in rankOrderMap;
}

/**
 * Get the image path for a Valorant rank
 * Returns the relative path without domain
 */
export const getValorantRankImagePath = (rank?: ValorantRank): string => {
  if (!rank) {
    return 'unranked/unranked';
  }

  switch (rank) {
    case 'UNRANKED':
      return 'unranked/unranked';

    case 'IRON_1':
      return 'iron/1';
    case 'IRON_2':
      return 'iron/2';
    case 'IRON_3':
      return 'iron/3';

    case 'BRONZE_1':
      return 'bronze/1';
    case 'BRONZE_2':
      return 'bronze/2';
    case 'BRONZE_3':
      return 'bronze/3';

    case 'SILVER_1':
      return 'silver/1';
    case 'SILVER_2':
      return 'silver/2';
    case 'SILVER_3':
      return 'silver/3';

    case 'GOLD_1':
      return 'gold/1';
    case 'GOLD_2':
      return 'gold/2';
    case 'GOLD_3':
      return 'gold/3';

    case 'PLATINUM_1':
      return 'platinum/1';
    case 'PLATINUM_2':
      return 'platinum/2';
    case 'PLATINUM_3':
      return 'platinum/3';

    case 'DIAMOND_1':
      return 'diamond/1';
    case 'DIAMOND_2':
      return 'diamond/2';
    case 'DIAMOND_3':
      return 'diamond/3';

    case 'ASCENDANT_1':
      return 'ascendant/1';
    case 'ASCENDANT_2':
      return 'ascendant/2';
    case 'ASCENDANT_3':
      return 'ascendant/3';

    case 'IMMORTAL_1':
      return 'immortal/1';
    case 'IMMORTAL_2':
      return 'immortal/2';
    case 'IMMORTAL_3':
      return 'immortal/3';

    case 'RADIANT':
      return 'radiant/radiant';

    default:
      return 'unranked/unranked';
  }
};

/**
 * Get the full ImageKit URL for a Valorant rank
 */
export const getValorantRankImageUrl = (rank?: ValorantRank): string => {
  const path = getValorantRankImagePath(rank);
  return `${imageKitUrlEndpoint}/valorant/ranks/${path}.png`;
};

/**
 * Get a human-readable label for a Valorant rank
 * Example: 'DIAMOND_3' -> "Diamond III"
 */
export const getRankLabel = (rank?: ValorantRank): string => {
  switch (rank) {
    case 'UNRANKED':
      return 'Unranked';

    case 'IRON_1':
      return 'Iron I';
    case 'IRON_2':
      return 'Iron II';
    case 'IRON_3':
      return 'Iron III';

    case 'BRONZE_1':
      return 'Bronze I';
    case 'BRONZE_2':
      return 'Bronze II';
    case 'BRONZE_3':
      return 'Bronze III';

    case 'SILVER_1':
      return 'Silver I';
    case 'SILVER_2':
      return 'Silver II';
    case 'SILVER_3':
      return 'Silver III';

    case 'GOLD_1':
      return 'Gold I';
    case 'GOLD_2':
      return 'Gold II';
    case 'GOLD_3':
      return 'Gold III';

    case 'PLATINUM_1':
      return 'Platinum I';
    case 'PLATINUM_2':
      return 'Platinum II';
    case 'PLATINUM_3':
      return 'Platinum III';

    case 'DIAMOND_1':
      return 'Diamond I';
    case 'DIAMOND_2':
      return 'Diamond II';
    case 'DIAMOND_3':
      return 'Diamond III';

    case 'ASCENDANT_1':
      return 'Ascendant I';
    case 'ASCENDANT_2':
      return 'Ascendant II';
    case 'ASCENDANT_3':
      return 'Ascendant III';

    case 'IMMORTAL_1':
      return 'Immortal I';
    case 'IMMORTAL_2':
      return 'Immortal II';
    case 'IMMORTAL_3':
      return 'Immortal III';

    case 'RADIANT':
      return 'Radiant';

    default:
      return 'Unranked';
  }
};
